import React, { useEffect } from 'react';
import {
    BUSINESS,
    DEFAULT_DESCRIPTION,
    DEFAULT_KEYWORDS,
    DEFAULT_TITLE,
    OG_IMAGE,
    SITE_NAME,
    SITE_URL,
} from '../../config/seo';

interface SeoProps {
    title?: string;
    description?: string;
    path?: string;
    noindex?: boolean;
}

const setMeta = (attribute: 'name' | 'property', key: string, content: string) => {
    const selector = `meta[${attribute}="${key}"]`;
    let element = document.head.querySelector(selector) as HTMLMetaElement | null;

    if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, key);
        document.head.appendChild(element);
    }

    element.setAttribute('content', content);
};

const setCanonical = (href: string) => {
    let element = document.head.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;

    if (!element) {
        element = document.createElement('link');
        element.setAttribute('rel', 'canonical');
        document.head.appendChild(element);
    }

    element.setAttribute('href', href);
};

const upsertJsonLd = (id: string, data: Record<string, unknown>) => {
    let script = document.getElementById(id) as HTMLScriptElement | null;

    if (!script) {
        script = document.createElement('script');
        script.id = id;
        script.type = 'application/ld+json';
        document.head.appendChild(script);
    }

    script.textContent = JSON.stringify(data);
};

const buildStructuredData = () => {
    const organization = {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        '@id': `${SITE_URL}/#organization`,
        name: SITE_NAME,
        url: SITE_URL,
        logo: OG_IMAGE,
        description: DEFAULT_DESCRIPTION,
        email: BUSINESS.email,
        telephone: BUSINESS.phone,
        sameAs: [BUSINESS.instagram],
    };

    const localBusiness = {
        '@context': 'https://schema.org',
        '@type': 'ClothingStore',
        '@id': `${SITE_URL}/#localbusiness`,
        name: SITE_NAME,
        url: SITE_URL,
        image: OG_IMAGE,
        description: DEFAULT_DESCRIPTION,
        telephone: BUSINESS.phone,
        email: BUSINESS.email,
        priceRange: '₹₹',
        address: {
            '@type': 'PostalAddress',
            streetAddress: BUSINESS.streetAddress,
            addressLocality: BUSINESS.addressLocality,
            addressRegion: BUSINESS.addressRegion,
            postalCode: BUSINESS.postalCode,
            addressCountry: BUSINESS.addressCountry,
        },
        geo: {
            '@type': 'GeoCoordinates',
            latitude: BUSINESS.geo.latitude,
            longitude: BUSINESS.geo.longitude,
        },
        areaServed: {
            '@type': 'AdministrativeArea',
            name: 'Tamil Nadu, India',
        },
        knowsAbout: [
            'Handloom sarees',
            'School uniform sets',
            'Custom textile design',
            'Bulk weaving orders',
            'Traditional handwoven fabrics',
        ],
    };

    const website = {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        '@id': `${SITE_URL}/#website`,
        name: SITE_NAME,
        url: SITE_URL,
        description: DEFAULT_DESCRIPTION,
        publisher: {
            '@id': `${SITE_URL}/#organization`,
        },
        inLanguage: 'en-IN',
    };

    const webPage = {
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        '@id': `${SITE_URL}/#webpage`,
        url: SITE_URL,
        name: DEFAULT_TITLE,
        description: DEFAULT_DESCRIPTION,
        isPartOf: {
            '@id': `${SITE_URL}/#website`,
        },
        about: {
            '@id': `${SITE_URL}/#localbusiness`,
        },
        inLanguage: 'en-IN',
    };

    return { organization, localBusiness, website, webPage };
};

const Seo: React.FC<SeoProps> = ({
    title = DEFAULT_TITLE,
    description = DEFAULT_DESCRIPTION,
    path = '/',
    noindex = false,
}) => {
    useEffect(() => {
        const canonicalUrl = `${SITE_URL}${path === '/' ? '/' : path}`;

        document.title = title;
        setCanonical(canonicalUrl);

        setMeta('name', 'description', description);
        setMeta('name', 'keywords', DEFAULT_KEYWORDS);
        setMeta('name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large');
        setMeta('name', 'googlebot', noindex ? 'noindex, nofollow' : 'index, follow');

        setMeta('property', 'og:type', 'website');
        setMeta('property', 'og:url', canonicalUrl);
        setMeta('property', 'og:title', title);
        setMeta('property', 'og:description', description);
        setMeta('property', 'og:image', OG_IMAGE);
        setMeta('property', 'og:site_name', SITE_NAME);
        setMeta('property', 'og:locale', 'en_IN');

        setMeta('name', 'twitter:card', 'summary_large_image');
        setMeta('name', 'twitter:title', title);
        setMeta('name', 'twitter:description', description);
        setMeta('name', 'twitter:image', OG_IMAGE);

        const structuredData = buildStructuredData();
        upsertJsonLd('seo-organization', structuredData.organization);
        upsertJsonLd('seo-localbusiness', structuredData.localBusiness);
        upsertJsonLd('seo-website', structuredData.website);
        upsertJsonLd('seo-webpage', structuredData.webPage);
    }, [title, description, path, noindex]);

    return null;
};

export default Seo;
